<template>
  <div class="lifecycle-wrap">
    <analytics-topbar
      title="Employee lifecycle"
      :sortKey="activeSort"
      :isFilterApplied="isFilterApplied"
      @toggleReportFilter="toggleReportFilters"
      @resetDashFilters="resetDashFilters"
    ></analytics-topbar>
    <div class="reportFilterDrawer">
      <reportFilters
        ref="ReportFilters"
        reportType="employee_lifecycle"
        @getReportWithFilter="getReportWithFilter"
        @sortOptions="sortOptions"
      ></reportFilters>
    </div>
    <div class="emp-life">
      <v-row class="lifecycle-heading">
        <v-col cols="12">
          <div class="lifecycle-title">
            Employee lifecycle
          </div>
          <div class="lifecycle-sub">
            Visualize how an employee engages with the company at different stages in an employee lifecycle.
          </div>
        </v-col>
      </v-row>
      <v-row class="lifecycle-body-wrap">
        <v-col cols="12">
          <avg-engagement></avg-engagement>
        </v-col>
      </v-row>
    </div>
    <v-row class="custom-chart">
      <v-col cols="9" style="position: absolute;">
        <div id="chartdiv" ref="lifecycleChart"></div>
      </v-col>
      <v-col cols="3" style="position: absolute;"></v-col>
    </v-row>
    <v-row class="lifecycle-heading">
      <v-col cols="12">
        <div class="lifecycle-title">
          Phases
        </div>
        <div class="lifecycle-sub">
          There are four phases in an employee lifecycle ( ELC) and each demonstrating the shifting needs of employees from onboarding to exit. Plan your strategies based on the actual needs of an employee during these phases.
        </div>
      </v-col>
    </v-row>
    <v-row class="lifecycle-body-wrap">
      <v-col cols="12">
        <phasesCard
          :sortByData="sortByData"
          :initialLoading="initialLoading"
          :data="phases.onboarding"
          :showReport="reports.onboarding"
          :tableData="getHeatMap('onboarding')"
          :allTableData="selectedTableData"
          @onViewReport="onViewReport"
          @onClickSortByDrivers="onClickSortByDrivers"
        ></phasesCard>
      </v-col>
    </v-row>
    <v-row class="lifecycle-body-wrap">
      <v-col cols="12">
        <phasesCard
          :sortByData="sortByData"
          :initialLoading="initialLoading"
          :data="phases.initial_development"
          :showReport="reports.initial_development"
          :tableData="getHeatMap('initial_development')"
          :allTableData="selectedTableData"
          @onViewReport="onViewReport"
          @onClickSortByDrivers="onClickSortByDrivers"
        ></phasesCard>
      </v-col>
    </v-row>
    <v-row class="lifecycle-body-wrap">
      <v-col cols="12">
        <phasesCard
          :sortByData="sortByData"
          :initialLoading="initialLoading"
          :data="phases.ongoing_development"
          :tableData="getHeatMap('ongoing_development')"
          :showReport="reports.ongoing_development"
          :allTableData="selectedTableData"
          @onViewReport="onViewReport"
          @onClickSortByDrivers="onClickSortByDrivers"
        ></phasesCard>
      </v-col>
    </v-row>
    <v-row class="lifecycle-body-wrap">
      <v-col cols="12">
        <phasesCard
          :sortByData="sortByData"
          :initialLoading="initialLoading"
          :data="phases.separation"
          :showReport="reports.separation"
          @onViewReport="onViewReport"
        ></phasesCard>
      </v-col>
    </v-row>
  </div>
</template>

<script>
/* eslint-disable */
/* eslint-disable quotes */
/* eslint-disable no-param-reassign */
// import Vue from 'vue';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';

import "amcharts3";
import "amcharts3/amcharts/serial";
import axios from 'axios';
// import { ContentLoader } from 'vue-content-loader';
import dayjs from 'dayjs';
import { mapState } from 'vuex';
import AnalyticsTopbar from './analytics-topbar';
import ReportFilters from './analytics-filters';
import avgEngagement from './life-cycle-component/avg-engagement';
// import driverAnalysis from './life-cycle-component/driver-analysis';
import phasesCard from './life-cycle-component/phases-card';
// import questionAnalysis from './life-cycle-component/question-analysis';

import AnalyticsMixin from './analytics-mixin';

// Vue.use(VDateRange);
am4core.useTheme(am4themes_animated);

export default {
  name: 'empLifecycle',

  mixins: [AnalyticsMixin],

  components: {
    AnalyticsTopbar,
    avgEngagement,
    ReportFilters,
    // driverAnalysis,
    phasesCard
    // questionAnalysis
  },

  data () {
    return {
      isFilterApplied: false,
      activeReport: null,
      activeSort: 'location',
      activeReportType: 'driver',
      selectedTableData: {},
      phases: {
        onboarding: {
          id: 'onboarding',
          title: 'Onboarding',
          sub: '<span>New employees,<span class="positive-SzAX_">&nbsp;0-3&nbsp;</span>months after starting.</span>',
          desc: 'Understand your onboarding initiatives, using driver engagement heatmap to truly learn what’s going well and what needs to be improved.',
          average: '7.5',
          disengaged: '25',
          participants: '50',
          icon: 'las la-handshake'
        },
        initial_development: {
          id: 'initial',
          title: 'Initial development',
          sub: '<span>Employees in the organization for<span class="positive-SzAX_">&nbsp;3-24&nbsp;</span>months.</span>',
          desc: 'First 24 months of an employment is typically a period where engagement is higher than average. Understand the specific drivers that will help you maximize this phase.',
          average: '7.5',
          disengaged: '28',
          participants: '58',
          icon: 'las la-chart-bar'
        },
        ongoing_development: {
          id: 'ongoing',
          title: 'Ongoing development',
          sub: '<span>Employees in the organization for<span class="positive-SzAX_">&nbsp;24+&nbsp;</span>months.</span>',
          desc: 'Understand which drivers will lengthen the development phase of employees. Ensure continued engagement, high performance, and assurance to the organization.',
          average: '7.5',
          disengaged: '58',
          participants: '53',
          icon: 'las la-seedling'
        },
        separation: {
          id: 'separation',
          title: 'Separation',
          sub: '<span>The final period of the employment.<span class="positive-SzAX_"></span></span>',
          desc: 'Learn which drivers/sub drivers were most persuasive in employees’ decisions to leave the organization. Proactively address these areas to shrink attrition going forward.',
          average: '0',
          disengaged: '0',
          participants: '0',
          icon: 'las la-object-ungroup'
        }
      },
      reports: {
        separation: false,
        ongoing_development: false,
        initial_development: false,
        onboarding: false
      },
      data: null,
      heatMApHeader: [],
      chart: [],
      driverBreakdown: {},
      questionWiseAnalysis: {},
      initialLoading: null,
      initialLoadingQuestion: null,
      showFilter: false,
      config: {
        panel: [],
        dateMenu: '',
        initialLoading: true,
        activeDriver: null
      },
      dateRangeOptions: {
        startDate: undefined,
        endDate: undefined,
        format: 'DD/MM/YYYY',
        minDate: dayjs('01/01/2018').format(),
        maxDate: dayjs().format(),
        presets: [
          {
            label: 'All time',
            range: [
              undefined,
              undefined
            ]
          },
          {
            label: 'This Week',
            range: [
              dayjs().subtract(7, 'day').format(),
              dayjs().format()
            ]
          },
          {
            label: 'Last 30 Days',
            range: [
              dayjs().subtract(1, 'month').format(),
              dayjs().format()
            ]
          },
          {
            label: 'Last 3 Months',
            range: [
              dayjs().subtract(3, 'month').format(),
              dayjs().format()
            ]
          },
          {
            label: 'Last 6 Months',
            range: [
              dayjs().subtract(6, 'month').format(),
              dayjs().format()
            ]
          }
        ],
        dateRange: {
          startDate: dayjs().format('DD/MM'),
          endDate: dayjs().format('DD/MM')
        }
      },
      filters: {
        default: {},
        selected: {},
        main: {}
      },
      filtersList: {
        default: {},
        selected: {},
        main: {}
      },
      searchFilter: '',
      sortByData: []
      // filterApplied: false
    };
  },

  created () {
    this.initialLoading = true;
    this.getLifecycleReport();
    this.$store.dispatch('updateReportFilters', null);
    // this.getFilters();
    // this.getFilterOptions();
  },

  computed: {
    ...mapState({
      user: state => state.user,
      updateReportFilters: state => state.updateReportFilters
    })
  },

  beforeDestroy () {
    am4core.disposeAllCharts();
  },

  methods: {
    sortOptions (val) {
      this.sortByData = val;
    },
    dashFiltersApplied (val) {
      this.isFilterApplied = val;
    },
    getReportWithFilter (val) {
      // console.log("filterApplied -> val", val);
      this.isFilterApplied = val;
      this.getLifecycleReport();
    },
    resetDashFilters () {
      this.filterApplied = false;
      this.$refs.ReportFilters.resetFilters();
    },
    toggleReportFilters () {
      this.$refs.ReportFilters.showFilterDrawer = true;
    },
    getHeatMap (val) {
      if (this.data && this.data[val] && this.data[val].engagement_heatmap) {
        return this.data[val].engagement_heatmap;
      }
      return {};
    },
    getHeatMapHeader (val) {
      if (this.data && this.data[val] && this.data[val].engagement_heatmap_headers) {
        return this.data[val].engagement_heatmap_headers;
      }
      return {};
    },
    updateDate (date) {
      if (date) {
        // eslint-disable-next-line prefer-destructuring
        this.dateRangeOptions.startDate = date[0];
        // eslint-disable-next-line prefer-destructuring
        this.dateRangeOptions.endDate = date[1];
      }
    },
    // getFilterName (name) {
    //   return name.replace('_', ' ');
    // },
    // getFilterOptions () {
    //   const queryParams = {
    //     options: 'sort_option,filter_options',
    //     report_name: 'employee_lifecycle'
    //   };
    //   axios.get(`${process.env.VUE_APP_API_URL}reports/get_report_options`, {
    //     params: queryParams
    //   }).then((response) => {
    //     console.log('response', response);
    //   });
    // },
    // getFilters () {
    //   axios.get(`${process.env.VUE_APP_API_URL}company/${this.user.company}/dashboard_filters_options`).then((response) => {
    //     if (response && response.data && response.data.queryset_filters_options) {
    //       this.config.initialLoadingFilters = false;
    //       this.filters.default = response.data.queryset_filters_options;

    //       const defaultFilters = {};
    //       this.$lodash.each(this.filters.default, (v, k) => {
    //         defaultFilters[k] = [];
    //       });
    //       this.$lodash.each(this.filters.selected, (v, k) => {
    //         defaultFilters[k] = v;
    //       });
    //       this.filters.selected = defaultFilters;
    //       const mainFilters = {};
    //       this.$lodash.each(this.filters.default, (v, k) => {
    //         mainFilters[k] = [];
    //       });
    //       this.filters.main = JSON.parse(JSON.stringify(mainFilters));
    //     }
    //   });
    // },
    // getFilterData (key) {
    //   // this.expansion = !this.expansion;
    //   axios.get(`${process.env.VUE_APP_API_URL}company/${this.user.company}/dashboard_filters?filter_field=${key}`).then((response) => {
    //     if (response && response.data && response.data) {
    //       this.config.initialLoadingFilters = false;
    //       this.filtersList.default = response.data.queryset_filters_options;
    //       const defaultFilters = {};
    //       this.$lodash.each(this.filters.default.data, (v, k) => {
    //         defaultFilters[k] = [];
    //       });
    //     }
    //   });
    // },
    onViewReport (id) {
      // this.activeReportType = 'driver';
      if (this.activeReport === id) {
        this.activeReport = id;
      } else {
        this.activeReport = id;
      }
      this.$lodash.each(this.reports, (val, key) => {
        this.reports[key] = false;
      });
      if (this.activeReport === 'onboarding') {
        // this.driverBreakdown = this.data.onboarding.driver_breakdown;
        this.selectedTableData = this.data.onboarding.engagement_heatmap_headers;
        this.reports.onboarding = true;
      }
      if (this.activeReport === 'initial') {
        this.selectedTableData = this.data.initial_development.engagement_heatmap_headers;
        this.driverBreakdown = this.data.initial_development.driver_breakdown;
        this.reports.initial_development = true;
      }
      if (this.activeReport === 'ongoing') {
        this.reports.ongoing_development = true;
        this.selectedTableData = this.data.ongoing_development.engagement_heatmap_headers;
        this.driverBreakdown = this.data.ongoing_development.driver_breakdown;
      }
    },
    onClickSortByDrivers (val) {
      this.activeSort = val;
      const queryParams = {
        sort_key: `session__user__user_profile__${val}` || undefined,
        initiated_date_start: this.updateReportFilters ? this.updateReportFilters.initiated_date_start : undefined,
        initiated_date_end: this.updateReportFilters ? this.updateReportFilters.initiated_date_end : undefined,
        // initiated_date_start: this.reportDate.start,
        // initiated_date_end: this.reportDate.end,
        user__profile__high_potential_emp: this.updateReportFilters ? this.updateReportFilters.high_potential_emp : undefined,
        fields: 'segment_points_analysis,engagement_score,segment_participation_stats,disengaged_employees_stats,segment_nps_breakdown,engagement_heatmap,drivers_analysis'
      };
      const filters = Object.assign({}, this.updateReportFilters);
      if (filters && filters.filters) {
        // filters.filters have the dynamic filters value
        delete filters.filters;
      }

      if (this.updateReportFilters && this.updateReportFilters.filters) {
        this.$lodash.each(this.updateReportFilters.filters, (v, k) => {
          if (v.length > 0) {
            queryParams[`user__profile__${k}__in`] = v.join();
          }
        });
      }
      axios.get(`${process.env.VUE_APP_API_URL}reports/employee_lifecycle_report`, {
        params: queryParams
      }).then((response) => {
        if (response && response.data) {
          this.data = response.data;
          this.onViewReport(this.activeReport);
        }
      }).catch((err) => {
        if (err.response.status === 429) {
          this.$store.dispatch('updateSnackbar', {
            color: 'error',
            show: true,
            text: err.response.data.error
          });
        } else {
          this.$store.dispatch('updateSnackbar', {
            color: 'error',
            show: true,
            text: 'Unable to get report, Please try again later!'
          });
        }
      });
    },
    createCustomChart (val) {
      // console.log('this.chart', this.chart);
      // window.AmCharts.makeChart("chartdiv", {
      //   "type": "serial",
      //   "theme": "none",
      //   "autoMarginOffset": 20,
      //   "zoomOnDoubleClick": false,
      //   "dataProvider": this.chart,
      //   "balloon": {
      //     "cornerRadius": 2,
      //     "fillAlpha": 1,
      //     "fillColor": "#fff",
      //     "textAlign": "left"
      //   },
      //   "valueAxes": [{
      //     "axisAlpha": 0,
      //     "gridColor": "#FFFFFF",
      //     "labelsEnabled": true,
      //     "minimum": 0,
      //     "maximum": 5,
      //     "autoGridCount": false,
      //     "gridCount": 6
      //   }],
      //   "graphs": [{
      //     "autoGridCount": false,
      //     "balloonText":
      //     "<div style='width: fit-content;padding:0!important;'><div style='padding:0 10px;font-size:12px;border-bottom: 1px dashed #e7e8e7;'>[[tempKey]] after starting</div><div style='padding: 0 10px;''><div><span style='padding-right: 10px;width:15px;'>[[driver_average]]</span><span>Driver average</span></div><div><span style='padding-right: 10px;width:15px;'>[[negative]]</span><span>Negative</span></div><div><span style='padding-right: 10px;width:15px;'>[[neutral]]</span><span>Neutral</span></div><div><span style='padding-right: 10px;width:15px;'>[[positive]]</span><span>Positive</span></div></div></div>",
      //     "bullet": "round",
      //     "bulletSize": 10,
      //     "connect": true,
      //     "lineColor": "#050550",
      //     "lineThickness": 2,
      //     "negativeLineColor": "#050550",
      //     "valueField": "value"
      //   }],
      //   "chartCursor": {
      //     "cursorAlpha": 0.1,
      //     "categoryBalloonAlpha": 0,
      //     "cursorColor": "#000",
      //     "fullWidth": true,
      //     "graphBulletSize": 1,
      //     "categoryBalloonEnabled": false,
      //     "color": "#fff",
      //     "zoomable": false
      //   },
      //   "zoomControl": {
      //     "zoomControlEnabled": false,
      //     "panControlEnabled": false
      //   },
      //   "dataDateFormat": "DD",
      //   "categoryField": "year",
      //   "categoryAxis": {
      //     "gridColor": "transparent",
      //     "axisColor": '#fff',
      //     "minPeriod": "DD",
      //     "parseDates": true,
      //     "minorGridEnabled": false,
      //     "labelsEnabled": true
      //   }
      // });
      var chart = am4core.create("chartdiv", am4charts.XYChart);
      chart.data = val;
      var dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.renderer.grid.template.disabled = true;
      dateAxis.renderer.labels.template.disabled = true;
      dateAxis.cursorTooltipEnabled = false;
      dateAxis.tooltip.disabled = true;

      var valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.renderer.minGridDistance = 40;
      valueAxis.renderer.grid.template.disabled = true;
      valueAxis.renderer.labels.template.disabled = true;
      valueAxis.min = 0;
      valueAxis.max = 5;
      valueAxis.strictMinMax = true;
      valueAxis.tooltip.disabled = true;
      var series = chart.series.push(new am4charts.LineSeries());
      series.dataFields.valueY = 'value';
      series.dataFields.dateX = 'year';
      series.name = name;
      series.tooltipHTML = `<center><strong>{tempKey}</strong></center>
      <hr />
      <table>
      <tr>
        <th align="left">Driver average</th>
        <td>{driver_average}</td>
      </tr>
      <tr>
        <th align="left">Disengaged</th>
        <td>{negative}</td>
      </tr>
      <tr>
        <th align="left">Passively engaged</th>
        <td>{neutral}</td>
      </tr>
      <tr>
        <th align="left">Highly engaged</th>
        <td>{positive}</td>
      </tr>
      </table>`;
      series.strokeWidth = 3;
      series.stroke = '#050550';
      series.fill = '#050550';
      var bullet = series.bullets.push(new am4charts.CircleBullet());
      bullet.circle.stroke = am4core.color('#fff');
      bullet.circle.strokeWidth = 0;
      // series.dataItems.template.locations.dateX = 0;
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.xAxis = dateAxis;
    },
    applyfilter () {
      this.filterApplied = true;
      this.showFilter = false;
      this.getLifecycleReport();
    },
    resetDashboard () {
      this.initialLoading = true;
      this.filterApplied = false;
      this.config.dateMenu = undefined;
      this.filters.selected = JSON.parse(JSON.stringify(this.filters.main));
      this.dateRangeOptions.startDate = undefined;
      this.dateRangeOptions.endDate = undefined;
      this.getLifecycleReport();
    },
    getLifecycleReport () {
      const queryParams = {
        initiated_date_start: this.updateReportFilters ? this.updateReportFilters.initiated_date_start : undefined,
        initiated_date_end: this.updateReportFilters ? this.updateReportFilters.initiated_date_end : undefined,
        user__profile__high_potential_emp: this.updateReportFilters ? this.updateReportFilters.high_potential_emp : undefined,
        fields: 'segment_points_analysis,engagement_score,segment_participation_stats,disengaged_employees_stats,segment_nps_breakdown,engagement_heatmap,drivers_analysis'
      };
      const filters = Object.assign({}, this.updateReportFilters);
      if (filters && filters.filters) {
        // filters.filters have the dynamic filters value
        delete filters.filters;
      }
      if (this.updateReportFilters && this.updateReportFilters.filters) {
        this.$lodash.each(this.updateReportFilters.filters, (v, k) => {
          if (v.length > 0 && k !== 'session__stage__title') {
            queryParams[`user__profile__${k}__in`] = v.join();
          } else {
            queryParams.stage__title__in = v.join();
          }
        });
      }
      axios.get(`${process.env.VUE_APP_API_URL}reports/employee_lifecycle_report`, {
        params: queryParams
      }).then((response) => {
        this.initialLoading = false;
        this.chart = [];
        this.data = response.data;
        this.createChartData('onboarding', 3);
        this.createChartData('initial_development', 8);
        this.createChartData('ongoing_development', 14);
        this.createCustomChart(this.chart);
        this.setPhasesData('onboarding');
        this.setPhasesData('initial_development');
        this.setPhasesData('ongoing_development');
      }).catch((err) => {
        if (err.response.status === 429) {
          this.$store.dispatch('updateSnackbar', {
            color: 'error',
            show: true,
            text: err.response.data.error
          });
        } else {
          this.$store.dispatch('updateSnackbar', {
            color: 'error',
            show: true,
            text: 'Unable to get report, Please try again later!'
          });
        }
      });
    },

    createChartData (type, step) {
      const pointsLength = [];
      const pointsPlots = [];
      let totalPoints = [];
      let plotLength = [];
      this.$lodash.each(this.data[type].segment_points_analysis, (val, key) => {
        let tempKey = key;
        tempKey = tempKey.replace(/m/g, " months");
        tempKey = tempKey.replace(/y/g, " years");
        pointsLength.push({ ...val, tempKey });
        totalPoints = pointsLength.length;
        const t = 6 / totalPoints;
        plotLength = parseInt(t, 10);
      });
      this.$lodash.each(pointsLength, (value, index) => {
        const idx = index - 1;
        let length = '';
        if (idx === -1) {
          length = step;
        } else {
          length = pointsPlots[idx].year + plotLength;
        }
        pointsPlots.push({ ...value, year: length });
      });
      this.$lodash.each(pointsPlots, (val) => {
        const t = {
          ...val
        };
        t.year = val.year;
        t.value = val.driver_average;
        this.chart.push(t);
      });
    },

    setPhasesData (type) {
      this.phases[type].average = this.data[type].engagement_score
        ? this.data[type].engagement_score : 0;
      // eslint-disable-next-line max-len
      this.phases[type].disengaged = this.data[type].disengaged_employees_stats.disengaged_employees_percent
        ? this.data[type].disengaged_employees_stats.disengaged_employees_percent : 0;
      this.phases[type].participants = this.data[type].chat_participation_stats.participation_rate
        ? this.data[type].chat_participation_stats.participation_rate : 0;
    },

    reportType (type, phase) {
      this.activeReportType = type;
      if (type === 'question') {
        this.initialLoadingQuestion = true;
        this.questionWiseAnalysis = [];
        this.getQuestionWiseReport(phase);
      }
    },

    getQuestionWiseReport (type) {
      axios.get(`${process.env.VUE_APP_API_URL}reports/employee_lifecycle_report`, {
        params: {
          stage_segment: type,
          fields: 'segment_questionwise_breakdown'
        }
      }).then((response) => {
        if (response) {
          this.initialLoadingQuestion = false;
          this.questionWiseAnalysis = response.data[type].segment_questionwise_breakdown;
        }
      }).catch((err) => {
        if (err.response.status === 429) {
          this.$store.dispatch('updateSnackbar', {
            color: 'error',
            show: true,
            text: err.response.data.error
          });
        } else {
          this.$store.dispatch('updateSnackbar', {
            color: 'error',
            show: true,
            text: 'Unable to get report, Please try again later!'
          });
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.lifecycle-wrap {
  background-color: var(--bg);
  .lifecycle-heading {
    margin: 0;
    .lifecycle-title {
      color: var(--color-text);
      // font-size: var(--content-size);
      font-size: var(--stats-size-1);
    }
    .lifecycle-sub {
      color: var(--color-text);
      font-size: var(--content-size);
      padding: 8px 0;
    }
  }
  .lifecycle-body-wrap {
    margin: 0;
  }
  .custom-chart {
    position: relative;
  }
}
.emp-life {
  // box-shadow: 0 4px 16px 11px rgba(0, 0, 0, 0.06),0 4px 8px rgba(0, 0, 0, 0.1) !important;
  // margin: 12px;
}
.layout-container {
  // margin: 0 10%;
}
.remove-chart {
  margin-top: -140px;
}
.report-head-wrap {
  .report-title {
    font-weight: 400;
    padding: 10px;
    padding-left: 0;
  }
  .report-sub {
    font-weight: 400;
    padding: 10px;
    padding-left: 0;
    padding-bottom: 20px;
  }
}

.chart-spacer {
  margin: 2rem 0;
}
#chartdiv {
  height: 160px;
  position: relative;
  top: -180.5px;
  a {
    display: none !important;
  }
}

.details-report-head-wrap {
  padding-top: 30px;
  .details-report-title {
    font-size: 24px;
    font-weight: 400;
  }
  .details-report-sub {
    font-weight: 400;
    font-size: 14px;
  }
}

.action-wrap {
  margin: 15px 0;
  background-color: #fff;
  border-radius: 4px;
  display: flex;
  justify-content: flex-end;
  height: 100%;
  min-height: 35px;
  .item {
    border: 1px solid #e7e8e7;
    align-items: center;
    color: #909da0;
    cursor: pointer;
    display: flex;
    flex: 0 0 auto;
    justify-content: center;
    padding: 8px;
    text-align: center;
  }
  .active {
    transition: 0.5s ease all;
    background-color: #050550;
    color: #fff;
  }
}
.filter-wrap {
  box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2),
    0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12);
  height: 50px;
}
.separator-2tVZk {
    margin: 30px 0;
    position: relative;
    height: 4px;
    .separatorLine-1lNyU {
      position: absolute;
      top: 0;
      left: -5px;
      width: 100%;
      height: 100%;
      background-image: linear-gradient(90deg,transparent 50%,#e7e8e7 0);
      background-size: 20px 100%;
      background-repeat: repeat;
  }
}
</style>

// <style lang="scss">
// .subdriver-tile {
//   .v-list__tile {
//     font-size: 12px !important;
//     height: 40px !important;
//     padding: 0px 10px !important;
//   }
// }

// .lifecycle-wrap {
//   overflow-y: auto;
//   height: 90vh;
//   .emoji-container {
//     max-height: 4em;
//     max-width: 4em;
//   }
//   .v-expansion-panel__header {
//     // background-color: #fff !important
//   }
// // .after-bg {
// //   margin-top: 8em;
// // }
// .filter-btn {
//   // right: 0;
//   // position: absolute;
//   // float: right;
//   // z-index: 100 !important;
//   // color:white;
// }
// .filter-drawer {
//   height: 0px!important;
//   overflow-y:hidden!important;
//   .filters-card {
//     height: 500px!important;
//   }
//   &.anonymous {
//     height: 200px!important;
//     .v-card {
//       height: 200px!important;
//     }
//   }
//   .v-expansion-panel {
//     height: 85%!important;
//     &.employee-filters {
//       .v-expansion-panel__container {
//         height: 100%!important;
//       }
//       .v-expansion-panel__header__icon {
//         display: none;
//         .v-icon {
//           color: rgba(0,0,0,0.10) !important;
//           font-size:16px !important;
//         }
//       }
//     }
//   }
// }
// }
// </style>
